import type { Theme } from '@mui/material';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { cardVariants } from '@/pages/ValuationsV2/theme';
import type { Country } from '@/types/GraphqlSchema';
import { combineSx } from '@/utils/combineSx';
import type { ValuationsFilters } from '../../store/valuationsStore';
import { DevelopmentStatusFilter } from './DevelopmentStatusFilter';
import { DevelopmentTypeFilter } from './DevelopmentTypeFilter';
import { FilterKey } from './filterConfig';
import { MinMaxFieldFilter } from './MinMaxFieldFilter';

const moreFiltersStyles = {
  root: (theme: Theme) => ({
    backgroundColor: theme.palette.primaryBackground.backgroundColor,
    borderTop: `1px solid ${theme.palette.baseColors.iceBlue.c100}`,
    borderBottom: `1px solid ${theme.palette.baseColors.iceBlue.c100}`,
    position: 'relative',
  }),
  grid: {
    gap: 'var(--gap-section)',
    padding: '0.75rem',
    maxWidth: '1600px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: {
      sm: '1fr',
      md: '1fr 1fr',
      lg: '1fr 1fr 2fr',
    },
    gridAutoFlow: {
      md: 'row',
    },
    gridAutoRows: {
      md: 'minmax(200px, 1fr)',
    },
  },
  subGrid: {
    display: 'grid',
    placeContent: 'start stretch',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridAutoFlow: 'row',
    gap: 'inherit',
  },
};

type MoreFiltersProps = {
  filters: ValuationsFilters;
  onChange: <K extends keyof ValuationsFilters>(key: K) => (value: ValuationsFilters[K]) => void;
  selectedCountries: Country[];
};

const MoreFilters = ({ filters, onChange, selectedCountries }: MoreFiltersProps) => {
  const handleNPVChange = useCallback(
    (min: string, max: string): void => {
      onChange(FilterKey.NpvMin)(min);
      onChange(FilterKey.NpvMax)(max);
    },
    [onChange],
  );

  const handleReservesChange = useCallback(
    (min: string, max: string): void => {
      onChange(FilterKey.ReservesMin)(min);
      onChange(FilterKey.ReservesMax)(max);
    },
    [onChange],
  );

  const handleProductionChange = useCallback(
    (min: string, max: string): void => {
      onChange(FilterKey.ProductionMin)(min);
      onChange(FilterKey.ProductionMax)(max);
    },
    [onChange],
  );

  const handleDepthChange = useCallback(
    (min: string, max: string): void => {
      onChange(FilterKey.DepthMin)(min);
      onChange(FilterKey.DepthMax)(max);
    },
    [onChange],
  );

  return (
    <Box sx={moreFiltersStyles.root} data-id='more-filters-root'>
      <Box sx={moreFiltersStyles.grid}>
        <DevelopmentStatusFilter
          value={filters[FilterKey.DevelopmentStatuses].map(f => ({ ...f, isDisabled: false }))}
          onChange={onChange(FilterKey.DevelopmentStatuses)}
          selectedCountries={selectedCountries}
          sx={combineSx(cardVariants({ interactive: false, padding: 'standard' }))}
        />
        <DevelopmentTypeFilter
          value={filters[FilterKey.DevelopmentTypes]}
          onChange={onChange(FilterKey.DevelopmentTypes)}
          sx={combineSx(cardVariants({ interactive: false, padding: 'standard' }))}
        />
        <Box sx={moreFiltersStyles.subGrid}>
          <MinMaxFieldFilter
            minValue={filters[FilterKey.NpvMin]}
            maxValue={filters[FilterKey.NpvMax]}
            onChange={handleNPVChange}
            label='NPV10 (US$MM)'
            dataId='npv-filter'
            sx={combineSx(cardVariants({ interactive: false, padding: 'standard' }))}
          />
          <MinMaxFieldFilter
            minValue={filters[FilterKey.ReservesMin]}
            maxValue={filters[FilterKey.ReservesMax]}
            onChange={handleReservesChange}
            label='RESERVES (MMboe)'
            dataId='reserves-filter'
            sx={combineSx(cardVariants({ interactive: false, padding: 'standard' }))}
          />
          <MinMaxFieldFilter
            minValue={filters[FilterKey.ProductionMin]}
            maxValue={filters[FilterKey.ProductionMax]}
            onChange={handleProductionChange}
            label='ESTIMATED RECENT PRODUCTION (boe/d)'
            dataId='production-filter'
            sx={combineSx(cardVariants({ interactive: false, padding: 'standard' }))}
          />
          <MinMaxFieldFilter
            minValue={filters[FilterKey.DepthMin]}
            maxValue={filters[FilterKey.DepthMax]}
            onChange={handleDepthChange}
            label='RESERVOIR DEPTH (m)'
            dataId='depth-filter'
            sx={combineSx(cardVariants({ interactive: false, padding: 'standard' }))}
          />
        </Box>
      </Box>
    </Box>
  );
};

export { MoreFilters };
